/**
 * Instancing helpers for the dense Community World (Graphics G2).
 *
 * Two strategies, each collapsing N placements of ONE asset into ONE draw call:
 *   • StaticInstancer — repeated STATIC props/foliage, via the engine's
 *     InstancedMeshPool (batches identical geometry+material into one InstancedMesh).
 *   • CrowdInstancer — the DYNAMIC Versona crowd: its own InstancedMesh + a per-frame
 *     count-packing frustum cull (off-screen instances fall outside [0,count) and are
 *     never submitted), reusing the engine's extractFrustumPlanes.
 *
 * Server-authority note: these instance purely DECORATIVE/derived geometry (a crowd of
 * NPCs whose authoritative positions come from `versona` rows; foliage deterministically
 * scattered from a server-declared `instance_group`). No authoritative count or world
 * object is invented client-side — structures/world objects still render per the model.
 */
import { makeAnchorGroup, onRenderAnchor, toAnchorLocal } from './local_origin.js';
import * as THREE from 'three/webgpu';
import { InstancedMeshPool } from 'enginex/render/batching/InstancedMeshPool.js';
import { attribute, float, select, fract, screenCoordinate } from 'three/tsl';
import { FADE_MS } from './lod_fade.js';
import { BatchedMeshPool } from 'enginex/render/batching/BatchedMeshPool.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { RAIN_SHELTER_LAYER } from './rain_shelter.js';

/** A sphere (point + `radius`) is inside the 6 normalized inward-facing planes when n·p+d ≥ −radius. */
export function inFrustum(planes: Float32Array, x: number, y: number, z: number, radius: number): boolean {
  for (let i = 0; i < 6; i++) {
    const o = i * 4;
    if (planes[o] * x + planes[o + 1] * y + planes[o + 2] * z + planes[o + 3] < -radius) return false;
  }
  return true;
}

/** The first renderable (geometry, material) under an object — what we instance. */
export function firstMesh(obj: THREE.Object3D): { geometry: THREE.BufferGeometry; material: THREE.Material; mesh: THREE.Mesh } | null {
  let found: { geometry: THREE.BufferGeometry; material: THREE.Material; mesh: THREE.Mesh } | null = null;
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!found && m.isMesh && m.geometry) found = { geometry: m.geometry, material: Array.isArray(m.material) ? m.material[0] : m.material, mesh: m };
  });
  return found;
}

/**
 * EVERY renderable (geometry, material, local-to-root matrix) under an object — for
 * instancing multi-part GLBs where `firstMesh` would silently drop every part but the
 * first. `root` must be identity-rooted (freshly loaded, never re-parented) so that
 * `child.matrixWorld` IS the child's local-to-root transform; the caller composes that
 * onto a per-placement world matrix to get each part's final instance matrix.
 */
export function collectMeshParts(root: THREE.Object3D): { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] {
  root.updateMatrixWorld(true);
  const out: { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && m.geometry) {
      out.push({ geometry: m.geometry, material: Array.isArray(m.material) ? m.material[0] : m.material, local: m.matrixWorld.clone() });
    }
  });
  return out;
}

/** Repeated static props/foliage → one InstancedMesh per (geometry, material) = one draw call. */
/**
 * Global "a new instanced batch was born" hook — wired once in main.ts to
 * schedulePipelineWarm. A NEW (geometry, material) batch is a NEW render
 * pipeline, and three r182 compiles pipelines SYNCHRONOUSLY at first view
 * (profiled: multi-second createRenderPipeline bursts while streaming an
 * area). Hooking the single chokepoint every spawner shares (decorations,
 * structures, graves, wilds, admin placements, settlements, foliage) covers
 * them all — including future spawners — with one wiring line.
 */
let newBatchHook: ((mesh?: THREE.Object3D) => void) | null = null;
export function setInstancerWarmHook(fn: ((mesh?: THREE.Object3D) => void) | null): void { newBatchHook = fn; }
export { FADE_MS };

// ── Crossfade support (LOD-switch research S5) ──
// Every batch renders a WRAPPER geometry (the source's attribute objects by
// reference — no data copy — plus one per-instance `aFade` float) with a
// FADE material (the cooked material cloned once, with a screen-door dither:
// interleaved gradient noise n per fragment; incoming aFade ∈ [0,1] keeps
// n < aFade, outgoing aFade ∈ [−2,−1] keeps n ≥ aFade + 2 — lib/lod_fade.ts
// is the JS mirror). At the steady state aFade = 1 keeps everything, so the
// dither costs a compare.
const fadeMats = new WeakMap<THREE.Material, THREE.Material>();
// Wrapper geometries are per OWNER (per StaticInstancer), not global.
//
// WHY (2026-09-14): `aFade` is a per-instance attribute that lives on the
// WRAPPER GEOMETRY, and the cache used to be keyed on the source geometry
// alone. Two instancers built over the same cooked GLB — settlement dressing
// and hinterland dressing share plenty — therefore handed their independent
// InstancedMeshes the SAME wrapper, and so the same `aFade` array. Their slot
// allocators are independent, so owner A fading out its slot 3 turned owner
// B's unrelated slot 3 invisible. The first caller's `capacity` also won, so a
// later owner with a larger maxPerBatch got an undersized fade array.
// (Adding capacity to the key would not have fixed it: two owners with equal
// capacity still collide. Ownership is the key.)
interface FadeOwnerState {
  byGeo: Map<THREE.BufferGeometry, THREE.BufferGeometry>;
  offs: Array<() => void>; // source-geometry dispose listeners, removed on release
}
const fadeOwners = new WeakMap<object, FadeOwnerState>();
export function fadeMaterialFor(material: THREE.Material): THREE.Material {
  let m = fadeMats.get(material);
  if (m) return m;
  m = material.clone();
  m.name = `${material.name || material.type}#fade`;
  // A classic Material.clone() copies only the class's own props: the custom
  // node slots the engine layers set on the source (colorNode from the detail
  // surface / snow accumulation, roughnessNode, normalNode …) would be lost on
  // the way into the instancer — the buildings rendered plain (shader wave
  // stage 2 probe, 2026-09-09). Carry every node slot across; the graphs (and
  // their uniforms, e.g. the snow coverage) are shared by design.
  {
    const src = material as unknown as Record<string, unknown>;
    const dst = m as unknown as Record<string, unknown>;
    for (const k of Object.keys(src)) if (k.endsWith('Node') && src[k] != null && dst[k] == null) dst[k] = src[k];
  }
  try {
    const f = attribute('aFade', 'float');
    const sc = screenCoordinate;
    const n = fract(float(52.9829189).mul(fract(sc.x.mul(0.06711056).add(sc.y.mul(0.00583715)))));
    // Mirror of lib/lod_fade.ts fadeKeep: incoming f ∈ [0,1] keeps n < f; outgoing f ∈ [−2,−1] keeps n ≥ f + 2.
    const keep = select(f.lessThan(-0.5), n.greaterThanEqual(f.add(2)), n.lessThan(f));
    const nm = m as unknown as { opacityNode?: unknown; alphaTestNode?: unknown };
    nm.opacityNode = select(keep, float(1), float(0));
    nm.alphaTestNode = float(0.5);
  } catch { /* a non-node material: no dither, still shares textures */ }
  fadeMats.set(material, m);
  return m;
}
export function fadeGeometryFor(owner: object, geometry: THREE.BufferGeometry, capacity: number): THREE.BufferGeometry {
  let st = fadeOwners.get(owner);
  if (!st) { st = { byGeo: new Map(), offs: [] }; fadeOwners.set(owner, st); }
  const cached = st.byGeo.get(geometry);
  if (cached) return cached;
  const g = new THREE.BufferGeometry();
  for (const name of Object.keys(geometry.attributes)) g.setAttribute(name, geometry.attributes[name]);
  if (geometry.index) g.setIndex(geometry.index);
  g.boundingSphere = geometry.boundingSphere;
  g.boundingBox = geometry.boundingBox;
  const fade = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
  fade.setUsage(THREE.DynamicDrawUsage);
  g.setAttribute('aFade', fade);
  g.name = `${geometry.name || 'geo'}#fade`;
  // The wrapper dies with its source (its shared attributes are the source's).
  const state = st;
  const onDispose = (): void => { fade.array = new Float32Array(0); state.byGeo.delete(geometry); };
  geometry.addEventListener('dispose', onDispose);
  st.offs.push(() => geometry.removeEventListener('dispose', onDispose));
  st.byGeo.set(geometry, g);
  return g;
}

/**
 * Drop an owner's wrapper geometries and the dispose listeners they installed
 * on the SOURCE geometries — those sources outlive the instancer (they are the
 * asset cache's), so without this a create/dispose cycle leaves a listener
 * behind on every geometry it ever touched.
 */
export function releaseFadeGeometries(owner: object): void {
  const st = fadeOwners.get(owner);
  if (!st) return;
  for (const off of st.offs) off();
  st.offs.length = 0;
  st.byGeo.clear();
  fadeOwners.delete(owner);
}

export class StaticInstancer {
  private readonly pool: InstancedMeshPool;
  private readonly maxPerBatch: number;
  private nextId = 1;
  // Matrices are ABSOLUTE at the API and anchor-relative in the GPU attribute
  // (lib/local_origin.ts — float32 instance matrices can't ride the scene
  // rebase; 6 cm vertex snapping at 720 km). The batches live under an anchor
  // group; `abs` keeps each instance's world matrix so a rebase can rewrite.
  private readonly anchor: { group: THREE.Group; release: () => void };
  private readonly abs = new Map<number, THREE.Matrix4>();
  private readonly tmp = new THREE.Matrix4();
  private readonly offAnchor: () => void;
  private readonly scene: THREE.Scene;
  /**
   * Shadow policy applied to every batch mesh as it is created (null = leave
   * three's defaults, i.e. neither cast nor receive).
   *
   * WHY A POLICY rather than per-call flags (2026-09-14): a batch is created
   * lazily, per (geometry, material) pair, deep inside `add()`. Settlement
   * buildings ended up casting and receiving NOTHING because no writer ever
   * reached those meshes — a whole town rendered with no shadows on or from
   * it. Setting the intent once on the instancer means a newly-encountered
   * part cannot be missed.
   */
  private shadowPolicy: { cast: boolean; receive: boolean } | null = null;
  /** Render wave 1 (2026-09-17): whether this instancer's batches are ROOFS for the rain-shelter bake (lib/rain_shelter.ts). */
  private rainLayer: boolean | null = null;
  constructor(scene: THREE.Scene, maxPerBatch = 4096, label = '') {
    this.scene = scene;
    this.maxPerBatch = maxPerBatch;
    this.anchor = makeAnchorGroup(`anchor:${label || 'instancer'}`);
    scene.add(this.anchor.group);
    // The pool only ever add()/remove()s its batch meshes on what it is handed.
    this.pool = new InstancedMeshPool(this.anchor.group as unknown as THREE.Scene, maxPerBatch, label);
    this.offAnchor = onRenderAnchor(() => { for (const [id, m] of this.abs) this.pool.updateMatrix(id, toAnchorLocal(m, this.tmp)); });
  }
  /**
   * Add one static instance of (geometry, material) at world `matrix`; returns
   * its handle (or -1 if the batch is full). `fade` is the crossfade value
   * (1 = solid; see lib/lod_fade.ts) — an instance born at 0 is invisible
   * until `setFade` ramps it.
   */
  add(geometry: THREE.BufferGeometry, material: THREE.Material, matrix: THREE.Matrix4, fade = 1): number {
    const id = this.nextId++;
    const before = this.pool.batchCount;
    const g = fadeGeometryFor(this, geometry, this.maxPerBatch), m = fadeMaterialFor(material);
    if (this.pool.add(id, g, m, toAnchorLocal(matrix, this.tmp)) < 0) return -1;
    this.abs.set(id, matrix.clone());
    // ALWAYS write the fade, even the default 1. WHY (2026-09-14): the array is
    // only filled with 1 at creation, and slots are RECYCLED — a slot whose
    // previous occupant faded out still holds its outgoing value (∈ [−2,−1]),
    // so skipping the write on `fade === 1` made the new occupant inherit an
    // invisible dither. One small buffer write per add is not worth a building
    // that never appears.
    this.pool.setInstanceAttribute(id, 'aFade', [fade]);
    // New pipeline → pre-warm JUST this batch (a whole-scene compile pays an
    // O(scene) walk per call — live-profiled as the dominant warm cost).
    if (this.pool.batchCount > before) {
      const batch = this.pool.meshFor(g, m) ?? undefined;
      if (batch && this.shadowPolicy) {
        (batch as THREE.Mesh).castShadow = this.shadowPolicy.cast;
        (batch as THREE.Mesh).receiveShadow = this.shadowPolicy.receive;
      }
      if (batch && this.rainLayer !== null) { if (this.rainLayer) batch.layers.enable(RAIN_SHELTER_LAYER); else batch.layers.disable(RAIN_SHELTER_LAYER); }
      newBatchHook?.(batch);
    }
    return id;
  }

  /**
   * Cast/receive shadows for every batch this instancer owns, now and for any
   * batch created later. Call it once, after construction.
   */
  setShadows(cast: boolean, receive: boolean): void {
    this.shadowPolicy = { cast, receive };
    for (const mesh of this.pool.meshes()) {
      (mesh as THREE.Mesh).castShadow = cast;
      (mesh as THREE.Mesh).receiveShadow = receive;
    }
  }
  /**
   * Put every batch this instancer owns, now and later, on the rain-shelter
   * layer (a ROOF for the top-down bake) — or take them off. Same policy
   * shape as `setShadows`, for the same reason: batches are created lazily.
   */
  setRainLayer(on: boolean): void {
    this.rainLayer = on;
    for (const mesh of this.pool.meshes()) { if (on) mesh.layers.enable(RAIN_SHELTER_LAYER); else mesh.layers.disable(RAIN_SHELTER_LAYER); }
  }
  /** Set an instance's crossfade value (+t incoming, −t outgoing, 1 solid). */
  setFade(id: number, fade: number): void { this.pool.setInstanceAttribute(id, 'aFade', [fade]); }
  /** The batch mesh an instance lives in (null when unknown). */
  batchOf(id: number): THREE.Object3D | null { return this.pool.slotOf(id)?.mesh ?? null; }
  /** The batch mesh backing (geometry, material), if built — targeted pre-compiles. */
  meshFor(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Object3D | null {
    return this.pool.meshFor(fadeGeometryFor(this, geometry, this.maxPerBatch), fadeMaterialFor(material));
  }
  remove(id: number): void { this.abs.delete(id); this.pool.remove(id); }
  get instanceCount(): number { return this.pool.totalInstances; }
  get batchCount(): number { return this.pool.batchCount; } // == draw calls for the instanced set
  dispose(): void { this.offAnchor(); this.anchor.release(); this.pool.dispose(); this.abs.clear(); releaseFadeGeometries(this); this.scene.remove(this.anchor.group); }
}

/**
 * Varied static geometry that shares ONE material → one THREE.BatchedMesh = one draw call.
 * All geometries must be consistently indexed (a BatchedMesh requirement) — cooked GLBs are.
 */
export class StaticBatcher {
  private readonly pool: BatchedMeshPool;
  private nextId = 1;
  // Same anchor discipline as StaticInstancer (a BatchedMesh's per-instance
  // matrices are a float32 texture).
  private readonly anchor: { group: THREE.Group; release: () => void };
  private readonly abs = new Map<number, THREE.Matrix4>();
  private readonly tmp = new THREE.Matrix4();
  private readonly offAnchor: () => void;
  private readonly scene: THREE.Scene;
  constructor(scene: THREE.Scene, material?: THREE.Material, maxInstances = 2048, maxVerts = 1 << 19, maxIndices = 1 << 20) {
    this.scene = scene;
    this.anchor = makeAnchorGroup('anchor:batcher');
    scene.add(this.anchor.group);
    this.pool = new BatchedMeshPool(this.anchor.group as unknown as THREE.Scene, maxVerts, maxIndices, maxInstances, material);
    this.offAnchor = onRenderAnchor(() => { for (const [id, m] of this.abs) this.pool.updateMatrix(id, toAnchorLocal(m, this.tmp)); });
  }
  /** Add one instance of `geometry` (deduped internally) at world `matrix`; returns its handle. */
  add(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): number {
    const geoId = this.pool.addGeometry(geometry); // BatchedMeshPool dedups by geometry.uuid
    const id = this.nextId++;
    this.pool.addInstance(id, geoId, toAnchorLocal(matrix, this.tmp));
    this.abs.set(id, matrix.clone());
    return id;
  }
  remove(id: number): void { this.abs.delete(id); this.pool.remove(id); }
  get instanceCount(): number { return this.pool.instanceCount; }
  get geometryCount(): number { return this.pool.geometryCount; }
  dispose(): void { this.offAnchor(); this.anchor.release(); this.pool.dispose(); this.abs.clear(); this.scene.remove(this.anchor.group); }
}

/** The dynamic Versona crowd: one InstancedMesh + per-frame count-packing frustum cull. */
export class CrowdInstancer {
  readonly mesh: THREE.InstancedMesh;
  private readonly mats: (THREE.Matrix4 | null)[] = [];
  private readonly px: number[] = []; private readonly py: number[] = []; private readonly pz: number[] = [];
  private readonly cullRadius: number;
  private readonly tmpLocal = new THREE.Matrix4();
  private readonly bounds = new THREE.Sphere(new THREE.Vector3(), 0);
  private _visible = 0;
  /** Ground-anchor lift (m) added to the staged ground Y at stamp time: centered
   *  geometry (the capsule) needs ~half its height; GLB avatar crowds pass the
   *  measured −bbox.min.y of their BAKED geometry (≈0 for feet-pivot models). */
  readonly yOffset: number;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, capacity: number, cullRadius = 1.5, yOffset = 0, label = '') {
    pinStaticMotionVectors(material); // count-packing reorders slots every frame: three's frozen previous matrices would streak every versona
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    if (label) this.mesh.name = `inst:${label}`; // census owner attribution
    // We cull per-instance ourselves against the MAIN camera; three's own test then runs against the bounds of
    // the packed survivors (cull() maintains them), which is what keeps a plaza crowd out of the shadow cascades
    // it is nowhere near — un-cullable, every body was drawn into all four maps.
    this.mesh.boundingSphere = this.bounds;
    this.mesh.frustumCulled = true;
    this.mesh.count = 0;
    this.cullRadius = cullRadius;
    this.yOffset = yOffset;
  }

  /** Drop all logical instances (rebuild each refresh). */
  clear(): void { this.mats.length = 0; this.px.length = 0; this.py.length = 0; this.pz.length = 0; }

  /** Set (matrix) or clear (null) logical instance i's world transform. */
  set(i: number, matrix: THREE.Matrix4 | null): void {
    if (matrix) {
      // Copy into a persistent slot — a clone() per versona per frame was
      // steady GC churn across every crowd (sweep-confirmed).
      const slot = this.mats[i] ?? (this.mats[i] = new THREE.Matrix4());
      slot.copy(matrix);
      this.px[i] = matrix.elements[12]; this.py[i] = matrix.elements[13]; this.pz[i] = matrix.elements[14];
    } else {
      this.mats[i] = null;
    }
  }

  get logicalCount(): number { let n = 0; for (const m of this.mats) if (m) n++; return n; }
  get visibleCount(): number { return this._visible; }

  /** Pack in-frustum instances to the front of the buffer + set .count → one draw call for the visible crowd. */
  cull(planes: Float32Array): number {
    let v = 0;
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let i = 0; i < this.mats.length; i++) {
      const m = this.mats[i];
      if (m && inFrustum(planes, this.px[i], this.py[i], this.pz[i], this.cullRadius)) {
        // Anchor-relative in the attribute (the mesh MUST hang under an anchor
        // group — lib/local_origin.ts); px/py/pz stay absolute for the cull.
        const e = toAnchorLocal(m, this.tmpLocal).elements;
        this.mesh.setMatrixAt(v, this.tmpLocal);
        if (e[12] < x0) x0 = e[12]; if (e[12] > x1) x1 = e[12];
        if (e[13] < y0) y0 = e[13]; if (e[13] > y1) y1 = e[13];
        if (e[14] < z0) z0 = e[14]; if (e[14] > z1) z1 = e[14];
        v++;
      }
    }
    this.mesh.count = v;
    this._visible = v;
    // The survivors' bounds, in the mesh's own (anchor-local) space: the box of their origins + a body's reach
    // (2× the cull radius: an instance origin is the FEET, and a scaled body stands up to ~2 m above them).
    if (v > 0) {
      this.bounds.center.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
      this.bounds.radius = Math.hypot(x1 - x0, y1 - y0, z1 - z0) / 2 + this.cullRadius * 2;
    }
    // Upload ONLY the packed prefix. Flagging the whole attribute re-sent the
    // FULL capacity buffer (1024 × 64 B) per crowd per frame even with zero
    // visible NPCs — ~78 MB/s of bufferSubData across the avatar crowds
    // (sweep-confirmed; the potato-walk profile's #2 blocker). count = 0 draws
    // nothing, so no upload is needed at all.
    if (v > 0) {
      const attr = this.mesh.instanceMatrix;
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, v * 16);
      attr.needsUpdate = true;
    }
    return v;
  }

  dispose(): void { this.mesh.dispose(); }
}
